import * as yup from "yup";
import axios from "axios";
import queryString from "query-string";
import { Progress, Report } from "../../../shared";
import { useLocation } from "react-router-dom";
import EPAIDLetter from "../../facility-profile/EPAIDLetter";
import { useState } from "react";
import { Modal, Button } from "react-bootstrap";

const cancelToken = axios.CancelToken;
let cancelBulkDownload = null;

export default function ConfirmLetterReport() {
    const [formData, setFormData] = useState({});
    const [errorMessage, setErrorMessage] = useState(null);
    const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);
    const [bulkDownloading, setBulkDownloading] = useState(false);
    const [bulkDownloadPercentage, setBulkDownloadPercentage] = useState(0);
    const [value] = useState(Math.round(Math.random() * (44 - 20)) + 20);
    const location = useLocation();
    const params = queryString.parse(location.search);
    const searchOnInit = Object.keys(params).length > 0;
    const initialFormValues = {
        facName: params.facName ?? "",
        idNumber: params.idNumber ?? "",
        issuedFrom: params.issuedFrom ?? "",
        issuedTo: params.issuedTo ?? ""
    };
    const schema = yup.object().shape({
        facName: yup.string(),
        idNumber: yup.string(),
        issuedFrom: yup.date().nullable().default(null),
        issuedTo: yup.date().nullable().default(null)
    });
    const formConfig = [
        {
            name: "facName",
            config: {
                label: "Facility Name",
                type: "text"
            }
        },
        {
            name: "idNumber",
            config: {
                label: "ID Number",
                type: "text"
            }
        },
        {
            name: "issuedFrom",
            config: {
                label: "Issued From",
                type: "date"
            }
        },
        {
            name: "issuedTo",
            config: {
                label: "Issued To",
                type: "date"
            }
        }
    ];

    const bulkDownload = () => {
        setBulkDownloading(true);
        setErrorMessage(null);
        const qs = queryString.stringify(formData);
        const axiosOptions = {
            responseType: "blob",
            cancelToken: new cancelToken(c => cancelBulkDownload = c),
            onDownloadProgress: e => {
                const percentComplete = Math.round((e.loaded * 100) / e.total);
                if (percentComplete > 70) {
                    setBulkDownloadPercentage(percentComplete);
                }
            }
        };
        axios
            .get(`/api/v1/hwts/search/facility/permanent-id-confirmations/bulk-download?${qs}`, axiosOptions)
            .then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", "confirmation-letters.zip");
                document.body.appendChild(link);
                link.click();
                link.remove();
                setShowBulkDownloadModal(false);
                setBulkDownloadPercentage(0);
                setBulkDownloading(false);
            })
            .catch(err => {
                if (!axios.isCancel(err)) {
                    console.error(err);
                    setErrorMessage("An error occurred, please try again later.");
                }
                setBulkDownloadPercentage(0);
                setBulkDownloading(false);
            });
    };

    const customButton = [
        {
            label: "Download All",
            onClick: fd => {
                setFormData(fd);
                setShowBulkDownloadModal(true);
            }
        }
    ];

    const customColumnDefs = [
        {
            headerName: "ID Number",
            field: "EPA_ID"
        },
        {
            headerName: "Facility Name",
            field: "FAC_NAME"
        },
        {
            headerName: "Issuance Date",
            field: "CREATE_DATE",
            cellRenderer: function(params) {
                return new Date(params.data.CREATE_DATE).toLocaleDateString();
            }
        },
        {
            headerName: "Download",
            field: "EPA_ID",
            cellRenderer: function(params) {
                return `<a href="${process.env.REACT_APP_DTSC_API_URL}/api/v1/hwts/search/facility/permanent-id-confirmation?epaId=${params.data.EPA_ID}" download><span class="ca-gov-icon-download" aria-hidden="true"></span></a>`;
            },
            cellRenderer_UseThisAfterWeUpgradeAgGrid: function(params) {
                /*
                    Our version of ag-grid does not allow returning JSX from cellRenderer - that comes in later versions.
                    We can revisit this after upgrading ag-grid-community or ag-grid-react
                    See the comments on Azure DevOps backlog item #36576
                */
                return <button onClick={() => EPAIDLetter(params.data.EPA_ID)}><span class="ca-gov-icon-download" aria-hidden="true"></span></button>;
            }
        }
    ];

    return <>
        <Report
            breadcrumb={[
                { name: "HWTS Reports", url: "/reports", active: false },
                { name: "Permanent State ID Confirmation Letters", url: "", active: true },
            ]}
            backToUrl="/reports"
            pageTitle="Permanent State ID Confirmation Letters"
            initialFormValues={initialFormValues}
            schema={schema}
            formConfig={formConfig}
            apiEndpoint="/api/v1/hwts/search/facility/permanent-id-confirmations"
            //customGridCells={customGridCells}
            searchOnInit={searchOnInit}
            customButton={customButton}
            //setRowDataCount={setRowDataCount}
            //reportDescription={reportDescription}
            customColumnDefs={customColumnDefs}
        />

        <div className="container">
            <Modal
                show={showBulkDownloadModal}
                onHide={_ => {
                    setShowBulkDownloadModal(false);
                    setErrorMessage(null);
                }}
                backdrop="static"
                keyboard={false}
                className="modal"
            >
                <Modal.Header className="modalHeader" closeButton>
                    <Modal.Title>Bulk Download</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {bulkDownloading ? (
                        <div className="DownloadMSG">
                            The letters are being downloaded.
                            <Progress
                                percent={bulkDownloadPercentage}
                                setProgressPercentage={setBulkDownloadPercentage}
                                value={value}
                                stop={false}
                                grid={false}
                            />
                        </div>
                    ) : (errorMessage ?? "Click the button below to begin downloading.")}
                </Modal.Body>
                <Modal.Footer>
                    {bulkDownloading ? (
                        <Button variant="secondary" onClick={() => cancelBulkDownload ?? cancelBulkDownload()}>Cancel</Button>
                    ) : (
                        <Button variant="primary" onClick={bulkDownload}>Download</Button>
                    )}
                </Modal.Footer>
            </Modal>
        </div>
    </>
}